import { Button, Checkbox } from "@blueprintjs/core";
import { DataTable, FieldSet, IDataTableColumn, useGridState } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import { UserMetierLocalitesGrid } from ".";
import { UserMetierDto, UtilisateurApi } from "../../../api";
import { BooleanColumn } from "../../../components";
import { useAuth } from "../../../contexts";
import { useApiService, useManageError, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";

export interface IProfileMetiersPageProps {}

export const ProfileMetiersPage: React.FunctionComponent<IProfileMetiersPageProps> = props => {
  const { t } = useTl();
  const api = useApiService(UtilisateurApi);
  const { user } = useAuth();
  const { manageError } = useManageError();
  const { id } = useParams<{ id: string; tab: string; state: string }>();
  const iduser = React.useMemo(() => +id, [id]);
  const [selectedIDs, setSelectedIDs] = React.useState<number[]>([]);

  const fetchMetiers = React.useCallback(() => api.utilisateurGetMetiers({ id: iduser }), [api, iduser]);
  const { data, isFetching, isRefetching, refetch } = useQuery(["user-metiers", iduser], fetchMetiers);

  const tableState = useGridState<UserMetierDto>({
    serverMode: false,
    enableFilter: true,
    pageSize: 50,
    availablePageSizes: [50, 100, 500, 1000],
    sortKeys: {
      nom: "ASC",
      stade: "ASC"
    },
    storageOptions: {
      columnCustomizationStorageMode: "state"
    }
  });

  React.useEffect(() => {
    if (data) {
      tableState.setData(data);
    }
  }, [data, tableState]);

  const toggleSelection = React.useCallback(
    async (item: UserMetierDto) => {
      if (!selectedIDs.includes(item.idmetier)) {
        setSelectedIDs(ids => [...ids, item.idmetier]);
      } else {
        setSelectedIDs(ids => ids.filter(id => id !== item.idmetier));
      }

      try {
        await api.utilisateurSaveUserMetier({ id: iduser, UserMetierDto: item });
        refetch();
      } catch (e) {
        manageError(e);
      }
    },
    [api, iduser, manageError, refetch, selectedIDs]
  );

  const columns = React.useMemo<IDataTableColumn<UserMetierDto>[]>(
    () => [
      {
        autoFitContent: true,
        computed: true,
        fieldName: "_toggle",
        render: (item: UserMetierDto, _, { subPanelOpened, toggleSubPanel }) => (
          <Button icon={subPanelOpened ? "chevron-down" : "chevron-right"} onClick={() => toggleSubPanel()} minimal />
        )
      },
      {
        computed: true,
        fieldName: "checkboxes",
        autoFitContent: true,
        render: (item: UserMetierDto) =>
          iduser === user.iduser ? (
            <Checkbox
              checked={selectedIDs.includes(item.idmetier) || item.selected}
              onChange={e => {
                toggleSelection(item);
              }}
            />
          ) : (
            <BooleanColumn value={item.selected} />
          )
      },
      {
        header: () => t(ETLCodes.Stade),
        fieldName: "stade",
        textEllipsisAfterWidth: "auto",
        autoFitContent: true
      },
      {
        header: () => t(ETLCodes.Indice),
        fieldName: "code",
        textEllipsisAfterWidth: "auto",
        autoFitContent: true
      },
      {
        header: () => t(ETLCodes.Intitule),
        fieldName: "nom"
      }
    ],
    [iduser, selectedIDs, t, toggleSelection, user.iduser]
  );

  return (
    <FieldSet title={!!data && t(ETLCodes.NbMetiers, { nb: data.length })}>
      <DataTable
        dateFormat="dd/MM/yyyy"
        tableState={tableState}
        loading={isFetching && !isRefetching}
        columns={columns}
        filterMode="OnEnter"
        keyFieldName="idmetier"
        onOpenSubComponent={(item: UserMetierDto) => (
          <UserMetierLocalitesGrid userId={iduser} metier={item}></UserMetierLocalitesGrid>
        )}
      />
    </FieldSet>
  );
};

export default ProfileMetiersPage;
